import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSubscription } from "../Context/SubscriptionContext";
import PremiumModal from "../components/access/PremiumModal";
import UpgradeScreen from "../components/access/UpgradeScreen";

const LaunchpadDetails = ({ selectedLaunchpad, onBack }) => {
  const navigate = useNavigate();
  const { canAccessFeature } = useSubscription();
  const [showUpgrade, setShowUpgrade] = useState(false);

  if (!selectedLaunchpad) return null;

  const handleTopicClick = (topic, index) => {
    // index 0 is Full Track, index 1 is first subtopic.
    // user wants 5 levels free. 
    // If index is 0, 1, 2, 3, 4, 5 (Full Track + first 5 topics)
    const hasAccess = canAccessFeature("LAUNCHPAD_MCQ", { techIndex: index });

    if (!hasAccess) {
      setShowUpgrade(true);
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const isTenantStudent = user && user.role === "student" && user.tenant;
      const targetPath = isTenantStudent ? "/student/skillgenlaunchpadlevels" : "/skillgenlaunchpadlevels";

      navigate(targetPath, {
        state: {
          track: selectedLaunchpad.table,
          topic,
          fullTrackName: selectedLaunchpad.name,
        },
      });
    } catch (err) {
      console.error("Navigation error:", err);
      alert("Something went wrong while navigating.");
    }
  };

  return (
    <div className="relative z-10">
      <button
        className="mb-8 px-5 py-2.5 bg-[#5B21B6] text-white font-bold rounded-xl hover:bg-[#4C1D95] transition shadow-lg shadow-purple-200 flex items-center gap-2 text-sm"
        onClick={onBack}
      >
        <ArrowLeft size={18} /> Back to Launchpads
      </button>

      <div className="mb-10">
        <h2 className="text-4xl font-black text-[#5B21B6] mb-2 uppercase tracking-tighter">
          {selectedLaunchpad.name}
        </h2>
        <div className="h-1.5 w-24 bg-[#5B21B6] rounded-full"></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Full Track Card */}
        <div
          className="bg-white p-8 rounded-[2rem] border-2 border-gray-100 hover:border-[#5B21B6] hover:shadow-xl hover:shadow-purple-100 cursor-pointer text-center transition-all duration-300 flex flex-col items-center group"
          onClick={() => handleTopicClick(selectedLaunchpad.name, 0)}
        >
          <div className="w-16 h-16 mb-4 bg-gray-50 rounded-2xl flex items-center justify-center group-hover:bg-purple-50 transition-colors">
            <span className="text-[#5B21B6] font-black text-2xl">FT</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">Full Track</h3>
          <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">
            Comprehensive Mastery
          </p>
        </div>

        {/* Subtopic Cards */}
        {selectedLaunchpad.topics.map((topic, index) => {
          const hasAccess = canAccessFeature("LAUNCHPAD_MCQ", {
            techIndex: index + 1,
          });
          return (
            <div
              key={index}
              className={`bg-white p-8 rounded-[2rem] border-2 transition-all duration-300 text-center flex flex-col items-center group relative ${
                hasAccess
                  ? "border-gray-100 hover:border-[#5B21B6] hover:shadow-xl hover:shadow-purple-100 cursor-pointer"
                  : "border-gray-50 opacity-60 cursor-not-allowed bg-gray-50"
              }`}
              onClick={() => hasAccess && handleTopicClick(topic, index + 1)}
            >
              {!hasAccess && (
                <span className="absolute top-6 right-6 text-xl">🔒</span>
              )}
              <div
                className={`w-16 h-16 mb-4 rounded-2xl flex items-center justify-center transition-colors ${
                  hasAccess
                    ? "bg-gray-50 group-hover:bg-purple-50 text-[#5B21B6]"
                    : "bg-gray-200 text-gray-400"
                }`}
              >
                <span className="font-black text-2xl">{index + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">{topic}</h3>
              <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest">
                {hasAccess ? "Unlocked" : "Premium"}
              </p>
            </div>
          );
        })}
      </div>

      <PremiumModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Unlock Premium Launchpad"
        message="Free users can access only the first 5 topics. Upgrade to premium for full access."
      >
        <div className="flex justify-center">
          <UpgradeScreen feature="LAUNCHPAD_MCQ" />
        </div>
      </PremiumModal>
    </div>
  );
};

export default LaunchpadDetails;




